import { useState, useEffect, useRef } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

const PILLAR_COLORS = [
  { accent: 'teal',   hex: '#14b8a6', light: 'bg-teal-500/20',   border: 'border-teal-500/40',   text: 'text-teal-400',   glow: 'shadow-teal-500/30'   },
  { accent: 'cyan',   hex: '#06b6d4', light: 'bg-cyan-500/20',   border: 'border-cyan-500/40',   text: 'text-cyan-400',   glow: 'shadow-cyan-500/30'   },
  { accent: 'emerald',hex: '#10b981', light: 'bg-emerald-500/20',border: 'border-emerald-500/40',text: 'text-emerald-400',glow: 'shadow-emerald-500/30'},
  { accent: 'amber',  hex: '#f59e0b', light: 'bg-amber-500/20',  border: 'border-amber-500/40',  text: 'text-amber-400',  glow: 'shadow-amber-500/30'  },
  { accent: 'violet', hex: '#8b5cf6', light: 'bg-violet-500/20', border: 'border-violet-500/40', text: 'text-violet-400', glow: 'shadow-violet-500/30' },
  { accent: 'rose',   hex: '#f43f5e', light: 'bg-rose-500/20',   border: 'border-rose-500/40',   text: 'text-rose-400',   glow: 'shadow-rose-500/30'   },
];

function PillarBadge({ number, color }: { number: number; color: typeof PILLAR_COLORS[0] }) {
  return (
    <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full ${color.light} ${color.border} border mb-6`}>
      <span className={`text-xs font-black uppercase tracking-widest ${color.text}`}>Pillar</span>
      <span className={`text-2xl font-black ${color.text}`}>{number}</span>
    </div>
  );
}

function SectionDivider({ color }: { color: typeof PILLAR_COLORS[0] }) {
  return (
    <div className="relative h-16 overflow-hidden">
      <div className={`absolute inset-x-0 top-1/2 h-px`} style={{ background: `linear-gradient(to right, transparent, ${color.hex}55, transparent)` }}></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className={`w-3 h-3 rounded-full ${color.light} border ${color.border} flex items-center justify-center`}>
          <div className={`w-1.5 h-1.5 rounded-full`} style={{ background: color.hex }}></div>
        </div>
      </div>
    </div>
  );
}

function GlowQuote({ text, color }: { text: string; color: typeof PILLAR_COLORS[0] }) {
  return (
    <div className={`relative rounded-2xl p-6 border ${color.border} overflow-hidden`} style={{ background: `linear-gradient(135deg, ${color.hex}12, ${color.hex}06)` }}>
      <div className="absolute top-3 left-4 opacity-20">
        <i className={`ri-double-quotes-l text-5xl ${color.text}`}></i>
      </div>
      <p className={`relative text-base sm:text-lg font-bold ${color.text} italic text-center leading-relaxed`}>{text}</p>
    </div>
  );
}

function StatCard({ value, label, color }: { value: string; label: string; color: typeof PILLAR_COLORS[0] }) {
  return (
    <div className={`rounded-xl p-5 border ${color.border} text-center`} style={{ background: `linear-gradient(135deg, ${color.hex}15, ${color.hex}05)` }}>
      <p className={`text-3xl font-black ${color.text} mb-1`}>{value}</p>
      <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
    </div>
  );
}

export default function PillarsPage() {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-pillars';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Six Pillars of Elite Baseball Performance | Elite Performance Lab",
      "description": "Explore the six foundational pillars of athlete development at Elite Performance Lab: Proprioception, AROM, Impulse Training, Fast Twitch Fibers, CNS Training, and Mentality & Process.",
      "url": `${import.meta.env.VITE_SITE_URL}/pillars`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Our Pillars", "item": `${import.meta.env.VITE_SITE_URL}/pillars` }
        ]
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "Six Pillars of Elite Performance",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Pillar 1: Proprioception Training", "description": "Controlling the body in time and space — the foundation of safe, efficient athletic movement." },
          { "@type": "ListItem", "position": 2, "name": "Pillar 2: Active Range of Motion (AROM)", "description": "Training and assessing usable range of motion for high school athletes during growth phases." },
          { "@type": "ListItem", "position": 3, "name": "Pillar 3: Impulse Training", "description": "Developing explosive force production in milliseconds — the driver of speed, power, and reactive movement." },
          { "@type": "ListItem", "position": 4, "name": "Pillar 4: Fast Twitch Muscle Fibers", "description": "Training Type IIa and IIx fibers for in-game explosive demands." },
          { "@type": "ListItem", "position": 5, "name": "Pillar 5: Central Nervous System (CNS)", "description": "Applied CNS training framework — rewarding the right system for peak performance output." },
          { "@type": "ListItem", "position": 6, "name": "Pillar 6: Mentality & Process", "description": "Mental performance, self-talk, visualization, and the three versions of yourself as an athlete." }
        ]
      }
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-pillars');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute('data-section-id');
            if (id) setVisibleSections((prev) => new Set([...prev, id]));
          }
        });
      },
      { threshold: 0.08 }
    );
    sectionRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 600);

      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(Math.min(progress, 100));
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const registerSection = (id: string) => (el: HTMLElement | null) => {
    if (el) {
      el.setAttribute('data-section-id', id);
      sectionRefs.current.set(id, el);
    }
  };

  const isVisible = (id: string) => visibleSections.has(id);

  const fadeIn = (id: string) =>
    `transition-all duration-700 ${isVisible(id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`;

  const c = PILLAR_COLORS;

  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-0 md:pt-40 bg-gray-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/fd4a2a4f47fb2f7daab41c5feb41a8fd.jpg"
            alt="Elite Performance Lab"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/60 via-gray-900/70 to-gray-900"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-5 lg:px-12">
          <div className="text-center mb-8">
            <span className="text-teal-400 text-xs font-bold uppercase tracking-widest">Our Foundation</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white text-center leading-tight mb-6 tracking-tight">
            Building Better<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-600">Athletes</span>
          </h1>
          <div className="flex items-center justify-center gap-4 mb-14">
            <div className="h-px w-16 bg-teal-500/40"></div>
            <div className="w-2 h-2 rounded-full bg-teal-400"></div>
            <div className="h-px w-16 bg-teal-500/40"></div>
          </div>

          <div className="bg-gray-800/60 backdrop-blur-sm border border-teal-500/20 rounded-2xl p-8 sm:p-12 mb-0">
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6">
              I am often asked how we consistently achieve the results we do at Elite Performance Lab. When I begin to explain our approach, I'm usually met with confused looks—and, more often than not, the unspoken reaction of <em className="text-gray-200">"that sounds stupid."</em> I typically just chuckle.
            </p>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6">
              Training ideologies have evolved repeatedly over decades—and even centuries. Some advancements have undeniably moved performance forward; others have done real harm. The reality is that most coaches and trainers simply teach what they were taught and how they were taught, without ever challenging whether it still serves the athlete in front of them.
            </p>
            <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-10">
              This outlines our pillars of development. Not in a specific order, as all pillars are equally important. Included are examples of drill work designed to create understanding—not just compliance. Our goal is not to give you a script, but a framework.
            </p>

            <div className="border-l-4 border-teal-500/60 pl-6 sm:pl-8 space-y-3 mb-10">
              {[
                "Do not limit yourself—or your players.",
                "Do your due diligence.",
                "Do your research.",
                "Add your own flair—but do not stray from the facts.",
                "Train the right systems for on-field performance.",
              ].map((line, i) => (
                <p key={i} className="text-white font-semibold text-base sm:text-lg">{line}</p>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 justify-center mb-10">
              {["Grow.", "Expand.", "Evolve."].map((word, i) => (
                <span key={i} className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-500 hover:from-teal-300 hover:to-cyan-400 transition-all duration-300 cursor-default">
                  {word}
                </span>
              ))}
            </div>

            <GlowQuote text='"Become the coach you wanted—and needed—when you were a player."' color={c[0]} />

            <p className="text-base sm:text-lg text-gray-300 leading-relaxed text-center mt-6">
              As trainers and coaches, we must constantly challenge our own ideologies and methodologies.{' '}
              <strong className="text-white">Growth begins when ego ends.</strong>{' '}
              If we are unwilling to evolve, we become the limitation.
            </p>
          </div>
        </div>
      </section>

      {/* Six Pillars Intro */}
      <section className="py-16 md:py-20 bg-gray-900">
        <div className="max-w-5xl mx-auto px-5 lg:px-12 text-center">
          <span className="text-teal-400 text-xs font-bold uppercase tracking-wider">The Framework</span>
          <h2 className="text-4xl sm:text-5xl font-black text-white mt-4 mb-6">
            The Six Pillars of Elite Performance
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-12">
            Our comprehensive approach to baseball performance is built on six fundamental pillars that work together to develop complete, resilient, and explosive athletes.
          </p>

          {/* Pillar nav pills */}
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { num: 1, label: 'Proprioception', color: c[0] },
              { num: 2, label: 'AROM',           color: c[1] },
              { num: 3, label: 'Impulse',         color: c[2] },
              { num: 4, label: 'Fast Twitch',     color: c[3] },
              { num: 5, label: 'CNS',             color: c[4] },
              { num: 6, label: 'Mentality',       color: c[5] },
            ].map(({ num, label, color }) => (
              <a
                key={num}
                href={`#pillar-${num}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border ${color.border} ${color.light} hover:scale-105 transition-transform duration-200 cursor-pointer`}
              >
                <span className={`text-xs font-black ${color.text}`}>{num}</span>
                <span className="text-xs text-gray-300 font-semibold">{label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ─── PILLAR 1 ─── */}
      <section id="pillar-1" className="py-16 md:py-20 bg-gray-800 relative overflow-hidden">
        {/* accent stripe */}
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: `linear-gradient(to bottom, transparent, ${c[0].hex}, transparent)` }}></div>

        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <div ref={registerSection('p1-hero')} className={fadeIn('p1-hero')}>
            <PillarBadge number={1} color={c[0]} />
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 flex items-center justify-center ${c[0].light} rounded-xl shadow-lg ${c[0].glow}`}>
                    <i className={`ri-speed-line text-3xl ${c[0].text}`}></i>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-white">Pillar 1: Proprioception Training</h2>
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${c[0].text} mb-3`}>Controlling the Body in Time and Space</h3>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  At the high school level, strength, speed, and power often increase faster than an athlete's ability to control them. Proprioception—the body's awareness of position, movement, and force—is what allows those physical qualities to be expressed efficiently and safely.
                </p>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  Without proper body awareness, increased strength and speed lead to compensations, excess stress on joints, slower recovery, and higher injury risk. Proprioception is the bridge between physical capacity and usable athletic performance.
                </p>
                <div className={`bg-gray-900/60 rounded-xl p-5 mb-5 border ${c[0].border}`}>
                  <p className="text-sm text-gray-300 font-semibold mb-2">Train from the ground up:</p>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Foot → Ankle → Knee → Hips → Lumbar Spine → Thoracic Spine → Shoulder → Head → Elbow → Hands
                  </p>
                </div>
                <GlowQuote text='"Strength without control is wasted potential."' color={c[0]} />
              </div>
              <div className="relative">
                <div className={`absolute -inset-2 rounded-3xl opacity-20 blur-xl`} style={{ background: c[0].hex }}></div>
                <img
                  src="https://readdy.ai/api/search-image?query=baseball%20athlete%20performing%20single%20leg%20balance%20proprioception%20drill%20on%20unstable%20surface%20in%20modern%20training%20facility%2C%20focused%20body%20control%20and%20stability%20exercise%2C%20professional%20sports%20performance%20environment%20with%20clean%20minimalist%20background%2C%20athletic%20concentration%20showing%20kinesthetic%20awareness%20and%20neuromuscular%20control&width=700&height=500&seq=pillar-proprio-001&orientation=landscape"
                  alt="Proprioception Training"
                  className="relative w-full h-80 object-cover rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>

          {/* Stats row */}
          <div ref={registerSection('p1-stats')} className={`mt-10 grid grid-cols-3 gap-4 ${fadeIn('p1-stats')}`}>
            <StatCard value="5" label="Training Levels" color={c[0]} />
            <StatCard value="10" label="Body Segments" color={c[0]} />
            <StatCard value="Daily" label="Recommended" color={c[0]} />
          </div>

          <div ref={registerSection('p1-principles')} className={`mt-12 bg-gray-900/60 rounded-2xl p-8 border ${c[0].border} ${fadeIn('p1-principles')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-lightbulb-line ${c[0].text}`}></i>
              High School Training Principles
            </h3>
            <ul className="space-y-3">
              {[
                "Quality of movement always precedes load, speed, or volume",
                "Control must be demonstrated before complexity is added",
                "Fatigue should never mask poor mechanics",
                "Regress when control is lost—progress when consistency is shown",
                "Foundational drills are never \"outgrown\"",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <i className={`ri-checkbox-circle-fill ${c[0].text} text-xl flex-shrink-0 mt-0.5`}></i>
                  <span className="text-gray-300 text-sm sm:text-base">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div ref={registerSection('p1-levels')} className={`mt-12 grid md:grid-cols-3 gap-6 ${fadeIn('p1-levels')}`}>
            {[
              { icon: 'ri-footprint-line', title: 'Foundational', sub: 'Mandatory for all athletes, all sports', items: ['Single-Leg Balance','Heel-to-Toe Walk','Jump Rope (Basic Rhythm)'] },
              { icon: 'ri-contrast-drop-2-line', title: 'Intermediate', sub: 'Instability, unilateral loading, controlled plyometrics', items: ['BOSU Ball Training','Rocker Board (Front/Back & Side)','Single-Leg Plyometrics','Jump Rope (Moderate Speed)'] },
              { icon: 'ri-rocket-line', title: 'Advanced', sub: 'Complexity, chaos, multi-planar demands', items: ['Single-Leg Box Jumps','Crossover Lunge Squats','Advanced Jump Rope','Bounding'] },
            ].map((card, i) => (
              <div key={i} className={`bg-gray-900 rounded-2xl p-6 border ${c[0].border} hover:shadow-lg hover:${c[0].glow} transition-all duration-300 hover:-translate-y-1`}>
                <div className={`w-12 h-12 flex items-center justify-center ${c[0].light} rounded-lg mb-4`}>
                  <i className={`${card.icon} text-2xl ${c[0].text}`}></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-1">{card.title}</h4>
                <p className="text-xs text-gray-400 mb-4 italic">{card.sub}</p>
                <ul className="space-y-2 text-sm text-gray-300">
                  {card.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className={`${c[0].text} font-bold`}>•</span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div ref={registerSection('p1-takeaway')} className={`mt-12 rounded-2xl p-8 border ${c[0].border} ${fadeIn('p1-takeaway')}`} style={{ background: `linear-gradient(135deg, ${c[0].hex}18, ${c[0].hex}08)` }}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-star-line ${c[0].text}`}></i>
              Key Takeaways
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "Proprioception converts strength into performance",
                "Control must precede chaos",
                "Regression is not failure—it's coaching",
                "Long-term athletic development requires patience",
              ].map((t, i) => (
                <div key={i} className="flex items-start gap-3">
                  <i className={`ri-arrow-right-s-line ${c[0].text} text-xl flex-shrink-0 mt-0.5`}></i>
                  <p className="text-gray-300 text-sm sm:text-base">{t}</p>
                </div>
              ))}
            </div>
            <p className={`text-base text-gray-200 font-semibold mt-6 text-center`}>
              High school athletes who master proprioception move more efficiently, recover faster, and stay healthier throughout their athletic careers.
            </p>
          </div>
        </div>
      </section>

      <SectionDivider color={c[1]} />

      {/* ─── PILLAR 2 ─── */}
      <section id="pillar-2" className="py-16 md:py-20 bg-gray-900 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1" style={{ background: `linear-gradient(to bottom, transparent, ${c[1].hex}, transparent)` }}></div>

        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <div ref={registerSection('p2-hero')} className={fadeIn('p2-hero')}>
            <PillarBadge number={2} color={c[1]} />
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="absolute -inset-2 rounded-3xl opacity-20 blur-xl" style={{ background: c[1].hex }}></div>
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_edd048fa645f2eb733a9d10d4c477369_fa07e643.jpg"
                  alt="Active Range of Motion Training"
                  className="relative w-full h-80 object-cover rounded-2xl shadow-2xl"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 flex items-center justify-center ${c[1].light} rounded-xl shadow-lg`}>
                    <i className={`ri-flashlight-line text-3xl ${c[1].text}`}></i>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-white">Pillar 2: Active Range of Motion (AROM)</h2>
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${c[1].text} mb-3`}>Training & Assessment for High School Athletes</h3>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  High school athletes exist in a high-risk, high-opportunity window. Rapid growth, increasing external load, higher training volumes, and sport specialization often outpace the athlete's ability to control newly acquired limb length and strength. AROM must now be trained intentionally, not assumed.
                </p>
                <div className={`bg-gray-800/60 rounded-xl p-5 mb-5 border ${c[1].border}`}>
                  <p className="text-sm font-bold text-white mb-3">Primary Goals:</p>
                  <ul className="space-y-2">
                    {[
                      "Maintain symmetrical, usable range of motion during growth spurts",
                      "Teach the athlete to own end ranges under control",
                      "Prepare joints and connective tissue for increased loading",
                      "Reduce overuse and non-contact injury risk",
                    ].map((g, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className={`${c[1].text} font-bold`}>•</span><span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <GlowQuote text='"Growth and strength gains mean nothing if the athlete cannot control their body through full, symmetrical ranges."' color={c[1]} />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div ref={registerSection('p2-stats')} className={`mt-10 grid grid-cols-3 gap-4 ${fadeIn('p2-stats')}`}>
            <StatCard value="Daily" label="AROM Exposure" color={c[1]} />
            <StatCard value="8–12 wk" label="Reassess Cycle" color={c[1]} />
            <StatCard value="2x/wk" label="Recovery Focus" color={c[1]} />
          </div>

          <div ref={registerSection('p2-assess')} className={`mt-12 bg-gray-800/60 rounded-2xl p-8 border ${c[1].border} ${fadeIn('p2-assess')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-search-eye-line ${c[1].text}`}></i>
              Assessment for High School Athletes
            </h3>
            <p className="text-gray-300 text-sm sm:text-base mb-6">Objective assessment becomes more important at this stage.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={`bg-gray-900/60 rounded-xl p-5 border ${c[1].border}`}>
                <p className="text-white font-semibold mb-3 flex items-center gap-2">
                  <i className={`ri-star-line ${c[1].text}`}></i>
                  Preferred Assessment
                </p>
                <p className="text-sm text-gray-300">MoCap analysis (e.g., Uplift Labs) for baseline and re-checks</p>
              </div>
              <div className={`bg-gray-900/60 rounded-xl p-5 border ${c[1].border}`}>
                <p className="text-white font-semibold mb-3 flex items-center gap-2">
                  <i className={`ri-checkbox-circle-line ${c[1].text}`}></i>
                  Minimum Standard Screens
                </p>
                <ul className="space-y-1 text-xs text-gray-300">
                  {["Squat (loaded and unloaded)","Lunge and split squat","Single-leg balance with trunk rotation","Overhead reach and shoulder flexion","Hip internal/external rotation","Thoracic rotation"].map((s,i)=>(
                    <li key={i}>• {s}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/30">
              <p className="text-red-400 font-bold mb-2 flex items-center gap-2"><i className="ri-alert-line"></i>Red Flags</p>
              <ul className="space-y-2 text-sm text-gray-300">
                {["Side-to-side asymmetries","Loss of range compared to prior assessments","Inability to control end ranges"].map((f,i)=>(
                  <li key={i} className="flex items-start gap-2"><span className="text-red-400">•</span><span>{f}</span></li>
                ))}
              </ul>
              <p className="text-sm text-gray-200 font-semibold mt-3 italic">Any limiting imbalance should be addressed before load or intensity is increased.</p>
            </div>
          </div>

          <div ref={registerSection('p2-drills')} className={`mt-12 ${fadeIn('p2-drills')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 text-center">High School AROM Drills</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon:'ri-time-line', title:'A) Static Stretching', items:['Short to moderate holds (10–20 seconds)','Used post-training or between sets if restrictions are present','Never aggressive or forced'] },
                { icon:'ri-arrow-left-right-line', title:'B) Active Stretching', badge:'Primary Tool', items:['Slow, controlled movement into end ranges','Emphasis on breathing and positional awareness'], extras:['Active hamstring sweeps','World\'s Greatest Stretch (controlled)','Controlled shoulder CARs'] },
                { icon:'ri-speed-line', title:'C) Dynamic Flow & Activation', desc:'Integrated into warm-ups to connect mobility with strength and coordination.', extras:['90°–90° hip rotations (progressing to transitions)','90°–90° to hip flexion','90°–90° to standing','Runway walks','Skips, hops, and bounds'] },
                { icon:'ri-basketball-line', title:'D) Sport-Specific Considerations', items:['Throwing athletes: Donley Protocol performed daily','Field/court athletes: Emphasize hips, ankles, thoracic spine','Collision sports: Prioritize neck, thoracic, and hip control'] },
              ].map((card, i) => (
                <div key={i} className={`bg-gray-800 rounded-2xl p-6 border ${c[1].border} hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`w-12 h-12 flex items-center justify-center ${c[1].light} rounded-lg mb-4`}>
                    <i className={`${card.icon} text-2xl ${c[1].text}`}></i>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-3">
                    {card.title}
                    {'badge' in card && <span className={`${c[1].text} text-sm ml-2`}>({card.badge})</span>}
                  </h4>
                  {'desc' in card && <p className="text-sm text-gray-300 mb-4">{card.desc as string}</p>}
                  {'items' in card && (
                    <ul className="space-y-2 text-sm text-gray-300 mb-3">
                      {(card.items as string[]).map((it, j) => (
                        <li key={j} className="flex items-start gap-2"><span className={`${c[1].text} font-bold`}>•</span><span>{it}</span></li>
                      ))}
                    </ul>
                  )}
                  {'extras' in card && (
                    <>
                      <p className="text-xs text-gray-400 font-semibold mb-2">Examples:</p>
                      <ul className="space-y-1 text-xs text-gray-300">
                        {(card.extras as string[]).map((e, j) => <li key={j}>• {e}</li>)}
                      </ul>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div ref={registerSection('p2-bottom')} className={`mt-12 rounded-2xl p-8 border ${c[1].border} text-center ${fadeIn('p2-bottom')}`} style={{ background: `linear-gradient(135deg, ${c[1].hex}18, ${c[1].hex}08)` }}>
            <h3 className="text-2xl font-bold text-white mb-4 flex items-center justify-center gap-3">
              <i className={`ri-star-line ${c[1].text}`}></i>
              Bottom Line
            </h3>
            <p className="text-base sm:text-lg text-gray-200 font-semibold max-w-4xl mx-auto">
              Growth and strength gains mean nothing if the athlete cannot control their body through full, symmetrical ranges. AROM is the gatekeeper to safe loading, durability, and long-term performance.
            </p>
          </div>
        </div>
      </section>

      <SectionDivider color={c[2]} />

      {/* ─── PILLAR 3 ─── */}
      <section id="pillar-3" className="py-16 md:py-20 bg-gray-800 relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: `linear-gradient(to bottom, transparent, ${c[2].hex}, transparent)` }}></div>

        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <div ref={registerSection('p3-hero')} className={fadeIn('p3-hero')}>
            <PillarBadge number={3} color={c[2]} />
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 flex items-center justify-center ${c[2].light} rounded-xl shadow-lg`}>
                    <i className={`ri-flashlight-fill text-3xl ${c[2].text}`}></i>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-white">Pillar 3: Impulse Training</h2>
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${c[2].text} mb-3`}>Performance & Drill Progressions</h3>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  Impulse is the force applied over a short period of time and is equal to the change in an object's momentum (Force × Time). In sport, impulse typically occurs in milliseconds and is a primary driver of explosive, fast-twitch, dynamic movement patterns.
                </p>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  Maximum speed matters—but the rate at which an athlete reaches maximum speed often matters more. In most competitive environments, athletes rarely have time to build velocity gradually.
                </p>
                <div className={`bg-gray-900/60 rounded-xl p-5 mb-5 border ${c[2].border}`}>
                  <p className="text-sm font-bold text-white mb-3">The ability to generate high force rapidly determines success in:</p>
                  <ul className="space-y-2">
                    {["Acceleration and re-acceleration","Jumps and landings","Cuts and change of direction","Swings, throws, and strikes"].map((g,i)=>(
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className={`${c[2].text} font-bold`}>•</span><span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <GlowQuote text='"Explosive speed, power, and reactive movement are all impulse-dependent qualities."' color={c[2]} />
              </div>
              <div className="relative">
                <div className="absolute -inset-2 rounded-3xl opacity-20 blur-xl" style={{ background: c[2].hex }}></div>
                <img
                  src="https://readdy.ai/api/search-image?query=high%20school%20athlete%20performing%20explosive%20training%20drills%20with%20a%20heavy%20water%20bag%20in%20a%20professional%20sports%20performance%20facility%2C%20dynamic%20power%20training%20using%20an%20unstable%20water-filled%20bag%20for%20core%20and%20full%20body%20strength%20development%2C%20athletic%20training%20environment%20with%20dramatic%20lighting%20showing%20intense%20focused%20effort%20and%20functional%20movement%20patterns%2C%20modern%20gym%20setting%20with%20turf%20flooring&width=700&height=500&seq=pillar-waterbag-003&orientation=landscape"
                  alt="Impulse Training with Water Bag"
                  className="relative w-full object-contain rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div ref={registerSection('p3-stats')} className={`mt-10 grid grid-cols-4 gap-4 ${fadeIn('p3-stats')}`}>
            <StatCard value="2x/wk" label="Frequency" color={c[2]} />
            <StatCard value="2–4 min" label="Rest Between Efforts" color={c[2]} />
            <StatCard value="ms" label="Impulse Duration" color={c[2]} />
            <StatCard value="3–5" label="Reps Per Drill" color={c[2]} />
          </div>

          <div ref={registerSection('p3-cats')} className={`mt-12 ${fadeIn('p3-cats')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Impulse Training Categories</h3>
            <div className="space-y-6">
              {[
                { icon:'ri-speed-up-line', title:'Sprint-Based Impulse (10–30 yards)', sub:'Focus: acceleration, re-acceleration, and projection', items:['Falling Starts','Kneeling Starts','Lateral Crossover Starts','Sprint → Decelerate → Re-accelerate'], note:'3–5 reps per drill with full recovery' },
                { icon:'ri-arrow-up-line', title:'Jumping, Bounding & Reactive Impulse', sub:'Focus: elastic response and force redirection', items:['Pogos (double → single leg): 2–3 × 15–20','Hurdle Hops (low height): 3 × 5–6','Broad Jump: Max distance × 3','Triple Broad Jump: Max distance × 3','Single-Leg Lateral Bounds: 3 × 3 each leg','Box Drop → Vertical Jump (12–18 in): 3 × 3–5'], note:'Reactive intent is encouraged only if landing quality is maintained' },
                { icon:'ri-basketball-line', title:'Medicine Ball (Moderate Load)', sub:'4–8 lb balls', items:['Rotational Scoop Toss','Overhead Back Toss','Slams (intent-driven)','Reverse Slams'], note:'3–4 sets of 4–6 reps' },
              ].map((cat, i) => (
                <div key={i} className={`bg-gray-900 rounded-2xl p-6 border ${c[2].border} hover:shadow-lg transition-all duration-300`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 flex items-center justify-center ${c[2].light} rounded-lg`}>
                      <i className={`${cat.icon} text-2xl ${c[2].text}`}></i>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white">{cat.title}</h4>
                      <p className="text-xs text-gray-400">{cat.sub}</p>
                    </div>
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2 text-sm text-gray-300 mb-3">
                    {cat.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <span className={`${c[2].text} font-bold`}>•</span><span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-400 italic">{cat.note}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider color={c[3]} />

      {/* ─── PILLAR 4 ─── */}
      <section id="pillar-4" className="py-16 md:py-20 bg-gray-900 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1" style={{ background: `linear-gradient(to bottom, transparent, ${c[3].hex}, transparent)` }}></div>

        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <div ref={registerSection('p4-hero')} className={fadeIn('p4-hero')}>
            <PillarBadge number={4} color={c[3]} />
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="absolute -inset-2 rounded-3xl opacity-20 blur-xl" style={{ background: c[3].hex }}></div>
                <img
                  src="https://readdy.ai/api/search-image?query=baseball%20athlete%20performing%20explosive%20fast%20twitch%20muscle%20training%20with%20maximum%20intensity%20sprint%20start%20and%20powerful%20acceleration%2C%20high-speed%20photography%20capturing%20peak%20athletic%20power%20output%20and%20neural%20activation%2C%20professional%20sports%20performance%20facility%20with%20dramatic%20lighting%20emphasizing%20explosive%20movement%20and%20muscular%20engagement%2C%20dynamic%20motion%20showing%20first-step%20quickness%20and%20reactive%20speed&width=700&height=500&seq=pillar-fasttwitch-004&orientation=landscape"
                  alt="Fast Twitch Training"
                  className="relative w-full h-80 object-cover rounded-2xl shadow-2xl"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 flex items-center justify-center ${c[3].light} rounded-xl shadow-lg`}>
                    <i className={`ri-flashlight-fill text-3xl ${c[3].text}`}></i>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-white">Pillar 4: Fast Twitch Muscle Fibers</h2>
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${c[3].text} mb-3`}>Training for In-Game Demands</h3>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  Success in sport depends on understanding what will make the most in-game impact for each athlete. Muscle fiber type plays a major role in determining an athlete's speed, endurance, and power — and how to best train for those traits.
                </p>
                <GlowQuote text='"High school athletes should not train like conditioned adults or playful youth—they must learn to express power repeatedly while protecting the nervous system."' color={c[3]} />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div ref={registerSection('p4-stats')} className={`mt-10 grid grid-cols-3 gap-4 ${fadeIn('p4-stats')}`}>
            <StatCard value="IIa" label="Repeat Power Fiber" color={c[3]} />
            <StatCard value="IIx" label="Max Explosive Fiber" color={c[3]} />
            <StatCard value="3–5:1" label="Rest-to-Work Ratio" color={c[3]} />
          </div>

          <div ref={registerSection('p4-principles')} className={`mt-12 bg-gray-800/60 rounded-2xl p-8 border ${c[3].border} ${fadeIn('p4-principles')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-lightbulb-line ${c[3].text}`}></i>
              High School Training Principles
            </h3>
            <ul className="space-y-3">
              {["Speed before strength","Intent before load","Quality before volume","Recover to express power","Train to access fast twitch fibers consistently, not exhaust them"].map((p,i)=>(
                <li key={i} className="flex items-start gap-3">
                  <i className={`ri-checkbox-circle-fill ${c[3].text} text-xl flex-shrink-0 mt-0.5`}></i>
                  <span className="text-gray-300 text-sm sm:text-base">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* IIa & IIx cards */}
          <div ref={registerSection('p4-fibers')} className={`mt-12 grid md:grid-cols-2 gap-8 ${fadeIn('p4-fibers')}`}>
            {/* IIa */}
            <div className={`bg-gray-800 rounded-2xl p-8 border ${c[3].border}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-12 h-12 flex items-center justify-center ${c[3].light} rounded-lg`}>
                  <i className={`ri-contrast-drop-2-line text-2xl ${c[3].text}`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white">Fast Twitch Type IIa</h3>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-6">
                {[{l:'Energy',v:'Aerobic + Anaerobic'},{l:'Role',v:'Repeated speed & power'},{l:'Fatigue',v:'Moderate'}].map((s,i)=>(
                  <div key={i} className="bg-gray-900/60 rounded-lg p-2 text-center border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">{s.l}</p>
                    <p className="text-xs text-white font-semibold">{s.v}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-3 text-sm text-gray-300">
                {["Squat, trap bar deadlift, split squat — 3–5 × 3–6 reps (70–85%)","8–20 lb med ball throws and slams — 3–5 × 6–10 reps","Hurdle hops, bounds, repeated jumps — 3–6 contacts/set","20–40 yd sprints — 6–10 reps with 60–90 sec rest"].map((it,i)=>(
                  <li key={i} className="flex items-start gap-2">
                    <span className={`${c[3].text} font-bold`}>•</span><span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* IIx */}
            <div className={`bg-gray-800 rounded-2xl p-8 border ${c[3].border}`}>
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-12 h-12 flex items-center justify-center ${c[3].light} rounded-lg`}>
                  <i className={`ri-rocket-line text-2xl ${c[3].text}`}></i>
                </div>
                <h3 className="text-2xl font-bold text-white">Fast Twitch Type IIx</h3>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {[{l:'Energy',v:'Anaerobic (ATP-CP)'},{l:'Role',v:'Max power & acceleration'},{l:'Fatigue',v:'Low'},{l:'Rest:Work',v:'3–5:1'}].map((s,i)=>(
                  <div key={i} className="bg-gray-900/60 rounded-lg p-2 text-center border border-gray-700">
                    <p className="text-xs text-gray-400 mb-1">{s.l}</p>
                    <p className="text-xs text-white font-semibold">{s.v}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-3 text-sm text-gray-300">
                {["Depth jumps, broad jumps, triple jumps","10–30 yd sprints (unresisted or light sled ≤20% BW)","Olympic lift variations — 2–4 reps with full rest","Max-effort rotational, overhead, scoop med ball throws","Partner chase, random start cues, COD reaction drills"].map((it,i)=>(
                  <li key={i} className="flex items-start gap-2">
                    <span className={`${c[3].text} font-bold`}>•</span><span>{it}</span>
                  </li>
                ))}
              </ul>
              <div className={`mt-4 rounded-xl p-4 border ${c[3].border}`} style={{ background: `${c[3].hex}15` }}>
                <p className={`text-sm font-bold ${c[3].text} italic text-center`}>End the session feeling fast, not tired.</p>
              </div>
            </div>
          </div>

          <div ref={registerSection('p4-limit')} className={`mt-12 bg-red-500/10 rounded-2xl p-8 border border-red-500/30 ${fadeIn('p4-limit')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className="ri-alert-line text-red-400"></i>
              What to Limit at the High School Level
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {["Excessive long-distance running","Conditioning that mimics fatigue instead of game speed","High-rep Olympic lifts","Daily max-effort lifting","Sprinting under fatigue"].map((it,i)=>(
                <div key={i} className="flex items-start gap-3">
                  <i className="ri-close-circle-fill text-red-400 text-xl flex-shrink-0 mt-0.5"></i>
                  <span className="text-gray-300 text-sm">{it}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-200 font-semibold mt-6 text-center">These blunt Type IIx output and slow athletes over time.</p>
          </div>

          <div ref={registerSection('p4-table')} className={`mt-12 rounded-2xl p-8 border ${c[3].border} ${fadeIn('p4-table')}`} style={{ background: `linear-gradient(135deg, ${c[3].hex}18, ${c[3].hex}08)` }}>
            <h3 className="text-2xl font-bold text-white mb-6 text-center">High School Fiber Development Summary</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className={`border-b ${c[3].border}`}>
                    <th className="pb-3 text-white font-bold text-sm">Fiber Type</th>
                    <th className="pb-3 text-white font-bold text-sm">Primary Role</th>
                    <th className="pb-3 text-white font-bold text-sm">Training Focus</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-gray-300">
                  {[
                    ['Type I','Aerobic base & recovery','Tempo runs, circuits, low-intensity work'],
                    ['Type IIa','Repeated power & speed','Strength, plyos, repeated sprints'],
                    ['Type IIx','Explosive speed & max power','Short sprints, jumps, Olympic variations'],
                  ].map(([type,role,focus],i)=>(
                    <tr key={i} className={`border-b border-gray-700/50`}>
                      <td className={`py-3 font-semibold ${c[3].text}`}>{type}</td>
                      <td className="py-3">{role}</td>
                      <td className="py-3">{focus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider color={c[4]} />

      {/* ─── PILLAR 5 ─── */}
      <section id="pillar-5" className="py-16 md:py-20 bg-gray-800 relative overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: `linear-gradient(to bottom, transparent, ${c[4].hex}, transparent)` }}></div>

        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <div ref={registerSection('p5-hero')} className={fadeIn('p5-hero')}>
            <PillarBadge number={5} color={c[4]} />
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 flex items-center justify-center ${c[4].light} rounded-xl shadow-lg`}>
                    <i className={`ri-mental-health-line text-3xl ${c[4].text}`}></i>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-white">Pillar 5: Central Nervous System (CNS)</h2>
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${c[4].text} mb-3`}>Applied Training Framework</h3>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  The CNS adapts to what it is rewarded for—output, timing, rhythm, and recovery patterns that mirror the game. Understanding how to train the nervous system is the difference between athletes who perform under pressure and those who fade.
                </p>
                <GlowQuote text='"Effort is NOT intent."' color={c[4]} />
                <div className={`mt-5 bg-gray-900/60 rounded-xl p-5 border ${c[4].border}`}>
                  <p className="text-sm font-bold text-white mb-3">Primary CNS Goals for High School Athletes:</p>
                  <ul className="space-y-2">
                    {["Improve rate of force development","Teach athletes how to apply intent under stress","Begin distinguishing IIa vs IIx demands"].map((g,i)=>(
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className={`${c[4].text} font-bold`}>•</span><span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -inset-2 rounded-3xl opacity-20 blur-xl" style={{ background: c[4].hex }}></div>
                <img
                  src="https://readdy.ai/api/search-image?query=baseball%20athlete%20in%20intense%20focused%20mental%20state%20during%20explosive%20sprint%20training%20showing%20neural%20activation%20and%20central%20nervous%20system%20engagement%2C%20high-speed%20photography%20capturing%20peak%20concentration%20and%20neuromuscular%20coordination%2C%20professional%20sports%20performance%20facility%20with%20dramatic%20lighting%20emphasizing%20mental%20focus%20and%20physical%20readiness%2C%20athletic%20intensity%20and%20CNS%20priming%20for%20maximum%20output&width=700&height=500&seq=pillar-cns-005&orientation=landscape"
                  alt="CNS Training"
                  className="relative w-full h-80 object-cover rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div ref={registerSection('p5-stats')} className={`mt-10 grid grid-cols-3 gap-4 ${fadeIn('p5-stats')}`}>
            <StatCard value="48 hrs" label="Recovery After 95% Sprint" color={c[4]} />
            <StatCard value="1:10+" label="IIx Work:Rest Ratio" color={c[4]} />
            <StatCard value="7–9 hrs" label="Sleep for Dopamine" color={c[4]} />
          </div>

          <div ref={registerSection('p5-effort')} className={`mt-12 rounded-2xl p-8 border ${c[4].border} ${fadeIn('p5-effort')}`} style={{ background: `linear-gradient(135deg, ${c[4].hex}18, ${c[4].hex}08)` }}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-flashlight-line ${c[4].text}`}></i>
              Effort & Intent
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { icon:'ri-contrast-drop-2-line', title:'IIa Days', items:['Work:Rest = 1:0.5 to 1:1','Slight breathlessness allowed','Stop when breathlessness or effort drops'] },
                { icon:'ri-rocket-line', title:'IIx Days', items:['Work:Rest = 1:5 to 1:10+','Max output only','Stop when speed or intent drops'] },
              ].map((day, i) => (
                <div key={i} className={`bg-gray-900/60 rounded-xl p-5 border ${c[4].border}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 flex items-center justify-center ${c[4].light} rounded-lg`}>
                      <i className={`${day.icon} text-xl ${c[4].text}`}></i>
                    </div>
                    <h4 className="text-lg font-bold text-white">{day.title}</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-300">
                    {day.items.map((it,j)=>(
                      <li key={j} className="flex items-start gap-2">
                        <span className={`${c[4].text} font-bold`}>•</span><span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* CNS Chemistry */}
          <div ref={registerSection('p5-chem')} className={`mt-12 ${fadeIn('p5-chem')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 text-center">CNS Chemistry: Practical Application</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Dopamine */}
              <div className="bg-gray-900 rounded-2xl p-8 border border-green-500/30">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-500/20 rounded-xl">
                    <i className="ri-accelerator-line text-3xl text-green-400"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white">Dopamine</h4>
                    <p className="text-sm text-green-400 font-semibold">Gas Pedal ⚡</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon:'ri-restaurant-line', title:'Diet', body:'Eggs, poultry, fish, dairy, beans + B6, Iron, Mg, Vit C' },
                    { icon:'ri-flashlight-line', title:'Training', body:'HIIT — short, intense, intentional' },
                    { icon:'ri-moon-line', title:'Sleep', body:'7–9 hours. Dopamine peaks in the morning.' },
                    { icon:'ri-eye-line', title:'Visualization', body:'Game-speed mental reps = CNS reps' },
                  ].map((item,i)=>(
                    <div key={i} className="bg-gray-800/60 rounded-xl p-4 border border-green-500/10">
                      <p className="text-white font-semibold mb-1 flex items-center gap-2 text-sm">
                        <i className={`${item.icon} text-green-400`}></i>{item.title}
                      </p>
                      <p className="text-xs text-gray-300">{item.body}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Serotonin */}
              <div className="bg-gray-900 rounded-2xl p-8 border border-red-500/30">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-red-500/20 rounded-xl">
                    <i className="ri-stop-circle-line text-3xl text-red-400"></i>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-white">Serotonin</h4>
                    <p className="text-sm text-red-400 font-semibold">Brake Pedal 🛑</p>
                  </div>
                </div>
                <div className="bg-gray-800/60 rounded-xl p-5 border border-red-500/10">
                  <ul className="space-y-2 text-sm text-gray-300">
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-fill text-red-400 text-base flex-shrink-0 mt-0.5"></i><span>Necessary—but excess causes lethargy and reduced output</span></li>
                    <li className="flex items-start gap-2"><i className="ri-close-circle-fill text-red-400 text-base flex-shrink-0 mt-0.5"></i><span><strong className="text-white">Chronic conditioning ≠ elite CNS performance</strong></span></li>
                  </ul>
                </div>
                <GlowQuote text='"Protect the CNS like the performance engine it is."' color={c[4]} />
              </div>
            </div>
          </div>

          {/* Coaching Takeaway */}
          <div ref={registerSection('p5-takeaway')} className={`mt-12 rounded-2xl p-8 border ${c[4].border} ${fadeIn('p5-takeaway')}`} style={{ background: `linear-gradient(135deg, ${c[4].hex}18, ${c[4].hex}08)` }}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-star-line ${c[4].text}`}></i>
              Coaching Takeaway
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/30">
                <p className="text-red-400 font-bold mb-3 flex items-center gap-2"><i className="ri-close-circle-line"></i>You do NOT train athletes harder by:</p>
                <ul className="space-y-2 text-sm text-gray-300">
                  {["Going longer","Grinding reps","Fatiguing systems that don't match the game"].map((it,i)=>(
                    <li key={i} className="flex items-start gap-2"><span className="text-red-400">•</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
              <div className={`rounded-xl p-5 border ${c[4].border}`} style={{ background: `${c[4].hex}15` }}>
                <p className="text-white font-bold mb-3 flex items-center gap-2"><i className="ri-check-line"></i>You train athletes BETTER by:</p>
                <ul className="space-y-2 text-sm text-gray-300">
                  {["Rewarding the right system","Matching impulse, rest, and environment","Protecting the CNS like the performance engine it is"].map((it,i)=>(
                    <li key={i} className="flex items-start gap-2"><span className={`${c[4].text}`}>•</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider color={c[5]} />

      {/* ─── PILLAR 6 ─── */}
      <section id="pillar-6" className="py-16 md:py-20 bg-gray-900 relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 w-1" style={{ background: `linear-gradient(to bottom, transparent, ${c[5].hex}, transparent)` }}></div>

        <div className="max-w-6xl mx-auto px-5 lg:px-12">
          <div ref={registerSection('p6-hero')} className={fadeIn('p6-hero')}>
            <PillarBadge number={6} color={c[5]} />
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="absolute -inset-2 rounded-3xl opacity-20 blur-xl" style={{ background: c[5].hex }}></div>
                <img
                  src="https://static.readdy.ai/image/4abb71d54c7d60621cfc0b2fab4f7970/338024c002d07d9b0d7f2bb1f0b7f192.png"
                  alt="Elite Athletes Operate as Three Different People"
                  className="relative w-full object-contain rounded-2xl shadow-2xl"
                />
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-14 h-14 flex items-center justify-center ${c[5].light} rounded-xl shadow-lg`}>
                    <i className={`ri-brain-line text-3xl ${c[5].text}`}></i>
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-black text-white">Pillar 6: Mentality &amp; Process</h2>
                </div>
                <h3 className={`text-xl sm:text-2xl font-bold ${c[5].text} mb-3`}>As a man thinks..... So he shall be.......</h3>
                <p className="text-base sm:text-lg text-gray-300 leading-relaxed mb-5">
                  Your mind is the control center of your body. The Central Nervous System (CNS) controls how your muscles fire, how fast you react, and how well you move. Your thoughts act like software. What you repeatedly think and say programs your nervous system for either confidence or hesitation.
                </p>
                <GlowQuote text='"As you think, so you perform."' color={c[5]} />
              </div>
            </div>
          </div>

          {/* Stats */}
          <div ref={registerSection('p6-stats')} className={`mt-10 grid grid-cols-3 gap-4 ${fadeIn('p6-stats')}`}>
            <StatCard value="Daily" label="Mental Training" color={c[5]} />
            <StatCard value="10–15 min" label="Reset Practice" color={c[5]} />
            <StatCard value="3 Versions" label="Of Yourself" color={c[5]} />
          </div>

          <div ref={registerSection('p6-lang')} className={`mt-12 bg-gray-800/60 rounded-2xl p-8 border ${c[5].border} ${fadeIn('p6-lang')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-chat-voice-line ${c[5].text}`}></i>
              Language Is Training
            </h3>
            <p className="text-gray-300 text-sm sm:text-base mb-6">What you say matters. What you say to yourself matters even more.</p>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {[
                { icon:'ri-mind-map', title:'Internal Self-Talk', body:'What you say in your head' },
                { icon:'ri-speak-line', title:'External Self-Talk', body:'What you say out loud' },
              ].map((card,i)=>(
                <div key={i} className={`bg-gray-900/60 rounded-xl p-5 border ${c[5].border}`}>
                  <p className="text-white font-semibold mb-3 flex items-center gap-2">
                    <i className={`${card.icon} ${c[5].text}`}></i>{card.title}
                  </p>
                  <p className="text-sm text-gray-300">{card.body}</p>
                </div>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className={`rounded-xl p-5 border ${c[5].border}`} style={{ background: `${c[5].hex}15` }}>
                <p className="text-white font-bold mb-2 flex items-center gap-2"><i className={`ri-check-line ${c[5].text}`}></i>Positive Mindset</p>
                <p className="text-sm text-gray-300">Does not guarantee success, but creates opportunity</p>
              </div>
              <div className="bg-red-500/10 rounded-xl p-5 border border-red-500/30">
                <p className="text-white font-bold mb-2 flex items-center gap-2"><i className="ri-close-line text-red-400"></i>Negative Mindset</p>
                <p className="text-sm text-gray-300">Guarantees underperformance</p>
              </div>
            </div>
            <p className="text-base text-gray-200 font-semibold text-center">
              Your brain searches for whatever you focus on: Focus on solutions → you improve. Focus on excuses → you stall.
            </p>
          </div>

          {/* Daily Discipline */}
          <div ref={registerSection('p6-discipline')} className={`mt-12 bg-gray-800 rounded-2xl p-8 border ${c[5].border} ${fadeIn('p6-discipline')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <i className={`ri-calendar-check-line ${c[5].text}`}></i>
              Daily Discipline Builds Confidence
            </h3>
            <div className={`bg-gray-900/60 rounded-xl p-5 mb-6 border ${c[5].border}`}>
              <p className="text-white font-semibold mb-3">Small Wins Early Create Momentum:</p>
              <ul className="space-y-2">
                {["Get out of bed when the alarm goes off — no snooze","Make your bed","Handle simple tasks immediately"].map((it,i)=>(
                  <li key={i} className="flex items-start gap-3">
                    <i className={`ri-checkbox-circle-fill ${c[5].text} text-xl flex-shrink-0 mt-0.5`}></i>
                    <span className="text-gray-300 text-sm">{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className={`rounded-xl p-6 border ${c[5].border}`} style={{ background: `linear-gradient(135deg, ${c[5].hex}18, ${c[5].hex}08)` }}>
              <p className="text-white font-bold mb-4">Look in the mirror and own it:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {["Today I'm locked in.","I'm prepared.","I'm disciplined.","I'm grateful for this opportunity.","I'm getting better every day."].map((a,i)=>(
                  <div key={i} className="flex items-start gap-2">
                    <i className={`ri-double-quotes-l ${c[5].text} text-lg flex-shrink-0 mt-0.5`}></i>
                    <p className="text-gray-200 text-sm font-semibold italic">{a}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-300 mt-4 text-center font-semibold">Say it until it becomes normal.</p>
            </div>
          </div>

          {/* Journaling + Visualization */}
          <div ref={registerSection('p6-journal')} className={`mt-12 grid md:grid-cols-2 gap-8 ${fadeIn('p6-journal')}`}>
            <div className={`bg-gray-800 rounded-2xl p-8 border ${c[5].border}`}>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                <i className={`ri-book-open-line ${c[5].text}`}></i>
                Journaling
              </h3>
              <div className="space-y-4">
                {[
                  { icon:'ri-sun-line', title:'Each Morning', items:['What is my main goal today?','What standard do I need to hold myself to?'] },
                  { icon:'ri-moon-line', title:'Each Night', items:['What did I do well?','Where did I fall short?','What adjustment do I make tomorrow?'] },
                ].map((block,i)=>(
                  <div key={i} className={`bg-gray-900/60 rounded-xl p-5 border ${c[5].border}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-8 h-8 flex items-center justify-center ${c[5].light} rounded-lg`}>
                        <i className={`${block.icon} text-xl ${c[5].text}`}></i>
                      </div>
                      <h4 className="text-base font-bold text-white">{block.title}</h4>
                    </div>
                    <ul className="space-y-2">
                      {block.items.map((it,j)=>(
                        <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                          <span className={`${c[5].text} font-bold`}>•</span><span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-300 font-semibold mt-4 text-center">Reflection creates awareness. Awareness drives growth.</p>
            </div>

            <div className={`bg-gray-800 rounded-2xl p-8 border ${c[5].border}`}>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
                <i className={`ri-eye-line ${c[5].text}`}></i>
                Visualization: Train Without Moving
              </h3>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { icon:'ri-hand-heart-line', title:'Feel', body:'Your stance and balance' },
                  { icon:'ri-volume-up-line', title:'Hear', body:'The environment' },
                  { icon:'ri-eye-line', title:'See', body:'Yourself execute with confidence' },
                  { icon:'ri-trophy-line', title:'Experience', body:'Success before it happens' },
                ].map((s,i)=>(
                  <div key={i} className={`bg-gray-900/60 rounded-xl p-4 border ${c[5].border} text-center`}>
                    <div className={`w-10 h-10 flex items-center justify-center ${c[5].light} rounded-lg mb-2 mx-auto`}>
                      <i className={`${s.icon} text-xl ${c[5].text}`}></i>
                    </div>
                    <p className="text-white font-semibold text-sm mb-1">{s.title}</p>
                    <p className="text-xs text-gray-300">{s.body}</p>
                  </div>
                ))}
              </div>
              <GlowQuote text='"Your nervous system responds to vivid visualization almost the same way it responds to real reps."' color={c[5]} />
            </div>
          </div>

          {/* Three Versions */}
          <div ref={registerSection('p6-versions')} className={`mt-12 ${fadeIn('p6-versions')}`}>
            <h3 className="text-2xl font-bold text-white mb-6 text-center">Three Versions of Yourself</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon:'ri-user-line', num:'1', title:'Without the Sport', body:'Your sport does not define your value. Identity outside the game creates balance, confidence, and emotional control. Strong students, teammates, friends, and family members make more resilient athletes.' },
                { icon:'ri-run-line', num:'2', title:'As a Training Athlete', body:'Training is where growth happens. Feedback is not criticism—it\'s information. Adjustments take time, and improvement requires consistency, intent, and patience.' },
                { icon:'ri-trophy-line', num:'3', title:'As a Competing Athlete', body:'Competition demands execution under pressure. Learning how to respond—mentally, emotionally, and physically—separates developing athletes from consistent competitors.' },
              ].map((v,i)=>(
                <div key={i} className={`bg-gray-800 rounded-2xl p-6 border ${c[5].border} hover:-translate-y-1 transition-transform duration-300`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 flex items-center justify-center ${c[5].light} rounded-lg`}>
                      <i className={`${v.icon} text-2xl ${c[5].text}`}></i>
                    </div>
                    <div>
                      <span className={`text-xs font-black ${c[5].text} uppercase tracking-widest`}>Version {v.num}</span>
                      <h5 className="text-base font-bold text-white">{v.title}</h5>
                    </div>
                  </div>
                  <p className="text-sm text-gray-300 leading-relaxed">{v.body}</p>
                </div>
              ))}
            </div>
          </div>

          <div ref={registerSection('p6-final')} className={`mt-12 rounded-2xl p-8 border ${c[5].border} ${fadeIn('p6-final')}`} style={{ background: `linear-gradient(135deg, ${c[5].hex}18, ${c[5].hex}08)` }}>
            <div className="text-center mb-6">
              <p className="text-base text-gray-200 font-semibold mb-2">Train your body in practice.</p>
              <p className="text-base text-gray-200 font-semibold mb-4">Train your mind every day.</p>
              <p className="text-2xl text-white font-black">You will perform how you think.</p>
            </div>
            <GlowQuote text='"When you understand your process, you don&#39;t just trust it - you own it."' color={c[5]} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-gray-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          {PILLAR_COLORS.map((col, i) => (
            <div key={i} className="absolute w-64 h-64 rounded-full blur-3xl" style={{ background: col.hex, left: `${i * 18}%`, top: i % 2 === 0 ? '10%' : '50%' }}></div>
          ))}
        </div>
        <div className="relative max-w-4xl mx-auto px-5 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">Ready to Build Your Foundation?</h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Our Six Pillars work together to create complete, resilient athletes who perform at their peak and stay healthy throughout their careers.
          </p>
          <a
            href="/#contact"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-bold text-base px-10 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Get Started Today
          </a>
        </div>
      </section>

      <Footer />

      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        aria-label="Back to top"
        className={`fixed bottom-8 right-8 z-50 w-12 h-12 flex items-center justify-center rounded-full bg-teal-500 hover:bg-teal-400 text-white shadow-lg hover:shadow-teal-500/40 transition-all duration-300 cursor-pointer ${
          showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <i className="ri-arrow-up-line text-xl"></i>
      </button>

      {/* Sticky Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-gray-800/80">
        <div
          className="h-full transition-all duration-100 ease-out"
          style={{
            width: `${scrollProgress}%`,
            background: 'linear-gradient(to right, #14b8a6, #06b6d4, #10b981, #f59e0b, #8b5cf6, #f43f5e)',
          }}
        />
      </div>
    </div>
  );
}
